import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  ChevronRight, CircleDot, Globe2, Server, ShieldCheck, X, Zap,
} from 'lucide-react';

import {
  loadWebserverAnalytics, loadWebserverOverview, loadWebserverRequests,
} from './api';
import {
  Empty, Failed, Head, Loading, Pill, WorkspaceTabs,
} from './parts';
import { tx } from './i18n';
import { countryName } from './countries';
import { useLiveResource } from './useLive';
import {
  CHART, COMPACT, ChartTip, FilterMenu, Flag, KpiRow, NUMBER, Panel, PulseMap, RangeTabs, Share, ago, counted, percent,
  stackRows, tipDay, tipHour, useNow,
} from './MobileParts';
import './webservers.css';

// The public analysis services from the outside, drawn with the Mobile app
// section's parts: where requests come from, how many there were and how they
// were answered, the services one by one, and every request as it arrived.

const MINUTE = 60000;
const HOUR = 3600000;
const DAY = 86400000;
const RANGES = [1, 7, 30, 180, 365];
const PAGE = 50;
const SHOWN = 20;
// Navy when answered, pale when sent elsewhere, the section's coral when it
// failed: lighter for the asker's mistake, darker for the server's.
const FAILED = '#d9957f';
const STATUS_COLORS = { 2: CHART.ink, 3: CHART.soft, 4: '#ecc3b0', 5: FAILED };

const COVERAGE_FIELDS = [
  ['ray', 'ui.webservers.ray_id'],
  ['country', 'ui.webservers.country'],
  ['city', 'ui.webservers.city'],
  ['region', 'ui.webservers.region'],
  ['coordinates', 'ui.webservers.coordinates'],
  ['timezone', 'ui.webservers.timezone'],
  ['asn', 'ui.webservers.asn'],
  ['botScore', 'ui.webservers.bot_score'],
  ['verifiedBot', 'ui.webservers.verified_bot'],
  ['ja4', 'ui.webservers.ja4_fingerprint'],
  ['cacheStatus', 'ui.webservers.cache_status'],
];

// Each field the edge adds, by the header it arrived in.
const EDGE_HEADERS = Object.freeze({
  connectingIp: 'CF-Connecting-IP',
  connectingIpv6: 'CF-Connecting-IPv6',
  pseudoIpv4: 'CF-Pseudo-IPv4',
  trueClientIp: 'True-Client-IP',
  countryCode: 'CF-IPCountry',
  city: 'CF-IPCity',
  continent: 'CF-IPContinent',
  latitude: 'CF-IPLatitude',
  longitude: 'CF-IPLongitude',
  region: 'CF-Region',
  regionCode: 'CF-Region-Code',
  metroCode: 'CF-Metro-Code',
  postalCode: 'CF-Postal-Code',
  timezone: 'CF-Timezone',
  ray: 'CF-Ray',
  colo: 'CF-Ray colo',
  asn: 'CF-Connecting-IP-ASN',
  visitor: 'CF-Visitor',
  worker: 'CF-Worker',
  cacheStatus: 'CF-Cache-Status',
  requestId: 'CF-Request-ID',
  requestPriority: 'CF-Request-Priority',
  botScore: 'CF-Bot-Score',
  threatScore: 'CF-Threat-Score',
  verifiedBot: 'CF-Verified-Bot',
  ja3Hash: 'CF-JA3-Hash',
  ja4: 'CF-JA4',
  exposedCredentialCheck: 'Exposed-Credential-Check',
  maliciousUploadsDetection: 'Malicious-Uploads-Detection',
  edgeVia: 'CF-EW-Via',
  cdnLoop: 'CDN-Loop',
});

// What the edge added to a request, under the name the webserver backend gives it.
function edgeOf(request) {
  return request.cloudflare || {};
}

function bytes(value) {
  const amount = Number(value || 0);
  if (amount < 1024) return `${NUMBER.format(amount)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let scaled = amount / 1024;
  let index = 0;
  while (scaled >= 1024 && index < units.length - 1) {
    scaled /= 1024;
    index += 1;
  }
  return `${scaled >= 100 ? scaled.toFixed(0) : scaled.toFixed(1)} ${units[index]}`;
}

function latency(value) {
  if (value === null || value === undefined) return '—';
  const microseconds = Number(value);
  if (microseconds < 1000) return `${Math.round(microseconds)} µs`;
  if (microseconds < 1000000) return `${(microseconds / 1000).toFixed(1)} ms`;
  return `${(microseconds / 1000000).toFixed(2)} s`;
}

function when(value) {
  return value === null || value === undefined ? tx('ui.webservers.no_activity') : new Date(value).toLocaleString();
}

function failures(item) {
  return Number(item.clientErrors || 0) + Number(item.serverErrors || 0);
}

function httpTone(status) {
  if (status >= 500) return 'bad';
  if (status >= 400) return 'warn';
  if (status >= 300) return 'info';
  return 'ok';
}

// The city a request came from, or its country when the city is not known.
function placeOf(request) {
  const edge = edgeOf(request);
  return edge.city || (edge.countryCode ? countryName(edge.countryCode) : '');
}

// The backend lists only the intervals that had requests; the chart and the
// trends need the quiet ones too. What was answered is what did not fail.
function filled(report) {
  const byAt = new Map(report.series.map((point) => [point.at, point]));
  const points = [];
  const first = Math.floor((report.generatedAt - report.days * DAY) / report.bucketMs) * report.bucketMs;
  for (let at = first; at <= report.generatedAt; at += report.bucketMs) {
    const point = byAt.get(at);
    points.push(point
      ? { ...point, answered: point.requests - point.errors }
      : { at, requests: 0, uniqueClients: 0, errors: 0, answered: 0 });
  }
  return points;
}

// A point's label: its hour within a day, its weekday and hour within a week, its day beyond.
function tickLabel(bucketMs, days) {
  if (bucketMs >= DAY) return (at) => new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  if (days <= 1) return (at) => new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return (at) => new Date(at).toLocaleString(undefined, { weekday: 'short', hour: '2-digit' });
}

// ---------------------------------------------------------------- traffic

// Each interval's requests, with what failed of them in coral. Visitors are
// the headline's trend: a handful of them would lie flat under the requests.
function TrafficChart({ report }) {
  const series = useMemo(() => filled(report), [report]);
  if (series.every((point) => point.requests === 0)) return <Empty title={tx('ui.webservers.no_traffic')} />;
  const label = tickLabel(report.bucketMs, report.days);
  return (
    <div className="xo-mob-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={CHART.grid} />
          <XAxis dataKey="at" tickFormatter={label} tickLine={false} axisLine={false} minTickGap={28} tick={CHART.tick} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={36} tickFormatter={(value) => COMPACT.format(value)} />
          <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
            <ChartTip title={(at) => (report.bucketMs < DAY ? tipHour(at) : tipDay(at))}
              rows={(point) => stackRows(point, [
                { key: 'answered', name: tx('ui.webservers.answered'), color: CHART.ink },
                { key: 'errors', name: tx('ui.webservers.failed'), color: FAILED },
              ])}
              facts={(point) => (point.requests === 0 ? [{ label: tx('ui.webservers.requests'), value: '0' }] : [
                { label: tx('ui.webservers.requests'), value: NUMBER.format(point.requests) },
                { label: tx('ui.webservers.visitors'), value: NUMBER.format(point.uniqueClients) },
                point.avgDurationUs === null || point.avgDurationUs === undefined ? null
                  : { label: tx('ui.webservers.avg_latency'), value: latency(point.avgDurationUs) },
                point.responseBytes ? { label: tx('ui.webservers.tip_served'), value: bytes(point.responseBytes) } : null,
              ])} />
          )} />
          <Bar dataKey="errors" stackId="requests" fill={FAILED} maxBarSize={22} name={tx('ui.webservers.failed')} />
          <Bar dataKey="answered" stackId="requests" fill={CHART.ink} radius={[3, 3, 0, 0]} maxBarSize={22} name={tx('ui.webservers.answered')} />
        </BarChart>
      </ResponsiveContainer>
      <div className="xo-mob-key">
        <span><i style={{ background: CHART.ink }} />{tx('ui.webservers.answered')}</span>
        <span><i style={{ background: FAILED }} />{tx('ui.webservers.failed')}</span>
      </div>
    </div>
  );
}

// Countries shaded by their requests, and a dot on every city requests came
// from; a dot pulses while that city's latest request is fresh.
function TrafficMap({ report, requests, height }) {
  const pings = useMemo(() => {
    const latest = new Map();
    for (const request of requests) {
      const edge = edgeOf(request);
      const key = `${edge.city}|${edge.countryCode}`;
      latest.set(key, Math.max(latest.get(key) || 0, request.createdAt));
    }
    return report.locations.map((spot) => ({
      at: latest.get(`${spot.city}|${spot.countryCode}`) || 0, city: spot.city, country: spot.countryCode,
      lat: spot.latitude, lng: spot.longitude, count: spot.requests,
    }));
  }, [report, requests]);
  return (
    <PulseMap countries={report.countries.filter((country) => country.code !== 'XX')} pings={pings} height={height}
      valueKey="requests" valueLabel={tx('ui.webservers.requests')} pingCountKey="ui.webservers.requests_count"
      label={tx('ui.webservers.map_label')} />
  );
}

// Where requests came from, by city or by country.
function Places({ report }) {
  const [by, setBy] = useState('city');
  const rows = by === 'city'
    ? report.cities.map((city) => ({
      key: `${city.countryCode}|${city.region}|${city.city}`, code: city.countryCode, name: city.city, requests: city.requests,
      detail: [city.region, countryName(city.countryCode)].filter(Boolean).join(' · '),
    }))
    : report.countries.filter((country) => country.code !== 'XX').map((country) => ({
      key: country.code, code: country.code, name: countryName(country.code), requests: country.requests,
      detail: counted('ui.webservers.visitor_count', country.uniqueClients),
    }));
  const shown = rows.slice(0, 5);
  const most = Math.max(1, ...shown.map((row) => row.requests));
  return (
    <Panel title={tx('ui.webservers.places_title')} detail={tx(by === 'city' ? 'ui.webservers.places_detail' : 'ui.webservers.places_countries_detail')}>
      <WorkspaceTabs value={by} onChange={setBy} label={tx('ui.webservers.places_title')} className="is-compact xo-mob-places-tabs" tabs={[
        { value: 'city', label: tx('ui.webservers.places_cities') },
        { value: 'country', label: tx('ui.webservers.places_countries') },
      ]} />
      {shown.length === 0 ? <Empty title={tx('ui.webservers.no_location_data')} /> : (
        <ol className="xo-mob-places">
          {shown.map((row) => (
            <li key={row.key}>
              <Flag code={row.code} />
              <span><strong>{row.name}</strong><small>{row.detail}</small></span>
              <Share value={row.requests} total={most} />
              <em>{counted('ui.webservers.requests_count', row.requests)}</em>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

// The requests of the last hour, from the newest ones read. The dot breathes
// while a request is fresh; a full page of them means there were more.
function LastHour({ requests, now, onOpen }) {
  const recent = requests.filter((request) => now - request.createdAt < HOUR);
  const fresh = requests.some((request) => now - request.createdAt < 15 * MINUTE);
  const latest = requests[0] || null;
  return (
    <section className="xo-mob-now">
      <header>
        <span className={`xo-mob-now-dot${fresh ? ' is-on' : ''}`} aria-hidden="true" />
        <span className="xo-mob-now-title"><strong>{tx('ui.webservers.live_title')}</strong></span>
        <strong className="xo-mob-now-count">{recent.length === PAGE ? `${PAGE}+` : NUMBER.format(recent.length)}</strong>
      </header>
      {recent.length > 0 ? (
        <ol className="xo-mob-now-list">
          {recent.map((request) => (
            <li key={request.id}>
              <button type="button" onClick={() => onOpen(request)}>
                <span className="xo-web-method">{request.method}</span>
                <span className="xo-mob-now-who">
                  <strong className="xo-web-code">{request.route || request.path}</strong>
                  <small><Flag code={edgeOf(request).countryCode} />{placeOf(request)}</small>
                </span>
                <span className="xo-mob-now-when">{ago(request.createdAt, now)}</span>
                <ChevronRight aria-hidden="true" />
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <p className="xo-mob-now-empty">
          {latest ? tx('ui.webservers.live_quiet', { when: ago(latest.createdAt, now) }) : tx('ui.webservers.live_none')}
        </p>
      )}
    </section>
  );
}

function LatestRequests({ requests, now, onOpen }) {
  if (requests.length === 0) return <Empty title={tx('ui.webservers.no_requests')} />;
  return (
    <ol className="xo-web-latest">
      {requests.map((request) => (
        <li key={request.id}>
          <button type="button" onClick={() => onOpen(request)}>
            <span className="xo-web-method">{request.method}</span>
            <span className="xo-web-latest-main">
              <strong className="xo-web-code">{request.route || request.path}</strong>
              <small><Flag code={edgeOf(request).countryCode} />{[placeOf(request), request.serviceName || tx('ui.webservers.unassigned')].filter(Boolean).join(' · ')}</small>
            </span>
            <Pill tone={httpTone(request.status)}>{request.status}</Pill>
            <span className="xo-web-latest-when">{ago(request.createdAt, now)}</span>
            <ChevronRight aria-hidden="true" />
          </button>
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------- services and answers

// One row per registered service; a row opens that service's analytics.
function Services({ services, days, now }) {
  const most = Math.max(1, ...services.map((service) => service.requests));
  return (
    <div className="xo-mob-table is-services" role="table">
      <div className="xo-mob-table-head" role="row">
        <span>{tx('ui.webservers.service')}</span><span>{tx('ui.webservers.requests')}</span><span>{tx('ui.webservers.visitors')}</span>
        <span>{tx('ui.webservers.submissions')}</span><span>{tx('ui.webservers.errors')}</span><span>{tx('ui.webservers.last_request')}</span><span />
      </div>
      {services.map((service) => {
        const failed = failures(service);
        return (
          <Link key={service.uuid} role="row" className="xo-mob-table-row"
            to={`/xomics/webservers/analytics?service=${encodeURIComponent(service.code)}&days=${days}`}>
            <span className="xo-web-cell-service">
              <span className="xo-mob-rank-icon"><Server aria-hidden="true" /></span>
              <span><strong>{service.displayName}</strong><small>{service.frontendPath}</small></span>
              {!service.active && <Pill tone="warn">{tx('ui.webservers.inactive')}</Pill>}
            </span>
            <span className="xo-web-cell-volume"><Share value={service.requests} total={most} /><strong>{NUMBER.format(service.requests)}</strong></span>
            <span className="xo-web-num is-extra">{NUMBER.format(service.uniqueClients)}</span>
            <span className="xo-web-num is-extra">{NUMBER.format(service.periodSubmissions)}</span>
            <span className={`xo-web-num${service.requests > 0 && failed / service.requests > 0.02 ? ' is-bad' : ''}`}>
              {service.requests === 0 ? '—' : percent(failed, service.requests)}
            </span>
            <span className="xo-web-num is-extra">{service.lastRequestAt ? ago(service.lastRequestAt, now) : '—'}</span>
            <ChevronRight aria-hidden="true" />
          </Link>
        );
      })}
    </div>
  );
}

// Every answer by its class, and the codes inside each class.
function Statuses({ statuses }) {
  const total = statuses.reduce((sum, row) => sum + row.requests, 0);
  if (total === 0) return <Empty title={tx('ui.webservers.no_traffic')} />;
  const classes = [2, 3, 4, 5].map((kind) => {
    const codes = statuses.filter((row) => Math.floor(row.status / 100) === kind).sort((a, b) => b.requests - a.requests);
    return { kind, codes, requests: codes.reduce((sum, row) => sum + row.requests, 0) };
  }).filter((entry) => entry.requests > 0);
  return (
    <div className="xo-web-statuses">
      <span className="xo-web-status-bar" aria-hidden="true">
        {classes.map((entry) => <i key={entry.kind} style={{ flexGrow: entry.requests, background: STATUS_COLORS[entry.kind] }} />)}
      </span>
      <ol className="xo-web-status-list">
        {classes.map((entry) => (
          <li key={entry.kind}>
            <i style={{ background: STATUS_COLORS[entry.kind] }} />
            <span>
              <strong>{tx(`ui.webservers.status_${entry.kind}`)}</strong>
              <span className="xo-web-codes">{entry.codes.map((row) => <b key={row.status}>{row.status}<span>{NUMBER.format(row.requests)}</span></b>)}</span>
            </span>
            <em>{NUMBER.format(entry.requests)}</em>
            <small>{percent(entry.requests, total)}</small>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Submissions({ submissions, now }) {
  if (submissions.length === 0) return <Empty title={tx('ui.webservers.no_submissions')} />;
  return (
    <ol className="xo-mob-rank is-tight xo-web-submissions">
      {submissions.map((submission) => (
        <li key={submission.uuid}>
          <span className="xo-mob-rank-icon"><Zap aria-hidden="true" /></span>
          <span className="xo-mob-rank-main">
            <strong>{submission.displayName || submission.serviceName}</strong>
            <small>{submission.serviceName}</small>
          </span>
          <span className="xo-web-latest-when">{ago(submission.createdAt, now)}</span>
        </li>
      ))}
    </ol>
  );
}

// Folded away: how many requests carry each field the edge adds.
function EdgeFields({ coverage }) {
  const total = Number(coverage.requests || 0);
  return (
    <details className="xo-mob-diagnostics">
      <summary>
        <ShieldCheck aria-hidden="true" />
        <span><strong>{tx('ui.webservers.edge_fields')}</strong><small>{tx('ui.webservers.edge_fields_detail')}</small></span>
        <Pill tone="flat">{counted('ui.webservers.requests_count', total)}</Pill>
      </summary>
      <div className="xo-mob-diagnostics-body">
        <ol className="xo-web-coverage">
          {COVERAGE_FIELDS.map(([field, labelKey]) => {
            const count = Number(coverage[field] || 0);
            return (
              <li key={field} className={count === 0 ? 'is-missing' : ''}>
                <span><strong>{tx(labelKey)}</strong><small>{total === 0 ? '—' : percent(count, total)}</small></span>
                <Share value={count} total={total} />
              </li>
            );
          })}
        </ol>
        <p className="xo-mob-footnote"><CircleDot aria-hidden="true" />{tx('ui.webservers.edge_note')}</p>
      </div>
    </details>
  );
}

// ---------------------------------------------------------------- analytics lists

function Routes({ routes }) {
  if (routes.length === 0) return <Empty title={tx('ui.webservers.no_traffic')} />;
  const shown = routes.slice(0, 6);
  const most = Math.max(1, ...shown.map((route) => route.requests));
  return (
    <ol className="xo-web-routes">
      {shown.map((route) => (
        <li key={route.route}>
          <span className="xo-web-route-main">
            <code className="xo-web-code">{route.route}</code>
            <small>
              {tx('ui.webservers.avg_value', { value: latency(route.avgDurationUs) })}
              {route.errors > 0 && <b>{` · ${counted('ui.webservers.error_count', route.errors)}`}</b>}
            </small>
          </span>
          <Share value={route.requests} total={most} />
          <em>{NUMBER.format(route.requests)}</em>
        </li>
      ))}
    </ol>
  );
}

// A short ranked list: a name, its share of the whole and its count.
function Ranked({ rows, empty }) {
  if (rows.length === 0) return <Empty title={empty} />;
  const total = rows.reduce((sum, row) => sum + row.requests, 0);
  const most = Math.max(1, ...rows.map((row) => row.requests));
  return (
    <ol className="xo-web-ranked">
      {rows.slice(0, 6).map((row) => (
        <li key={row.key}>
          <span className="xo-web-ranked-name">{row.name}</span>
          <Share value={row.requests} total={most} />
          <em>{NUMBER.format(row.requests)}</em>
          <small>{percent(row.requests, total)}</small>
        </li>
      ))}
    </ol>
  );
}

// A referring page as its site, with the path underneath.
function referrerName(referrer) {
  try {
    const url = new URL(referrer);
    return <><strong>{url.host}</strong><small>{url.pathname === '/' ? '' : url.pathname}</small></>;
  } catch (_error) {
    return <strong>{referrer}</strong>;
  }
}

function RequestTable({ requests, now, onOpen }) {
  if (requests.length === 0) return <Empty title={tx('ui.webservers.no_requests')} />;
  return (
    <div className="xo-mob-table is-requests" role="table">
      <div className="xo-mob-table-head" role="row">
        <span>{tx('ui.webservers.request')}</span><span>{tx('ui.webservers.location')}</span>
        <span>{tx('ui.webservers.status')}</span><span>{tx('ui.webservers.latency')}</span><span />
      </div>
      {requests.map((request) => (
        <button type="button" key={request.id} role="row" className="xo-mob-table-row" onClick={() => onOpen(request)}>
          <span className="xo-web-cell-request">
            <span className="xo-web-method">{request.method}</span>
            <span>
              <strong className="xo-web-code">{request.route || request.path}</strong>
              <small>{request.serviceName || tx('ui.webservers.unassigned')} · {ago(request.createdAt, now)}</small>
            </span>
          </span>
          <span className="xo-web-cell-place is-extra"><Flag code={edgeOf(request).countryCode} /><span>{placeOf(request)}</span></span>
          <span><Pill tone={httpTone(request.status)}>{request.status}</Pill></span>
          <span className="xo-web-cell-latency">{latency(request.durationUs)}</span>
          <ChevronRight aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- one request

function Fields({ title, rows, mono = false }) {
  if (rows.length === 0) return null;
  return (
    <section>
      <h3 className="xo-mob-subhead">{title}</h3>
      <dl className={`xo-web-fields${mono ? ' is-mono' : ''}`}>
        {rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
      </dl>
    </section>
  );
}

// Everything one request carried: how it was answered, what the edge added and what the browser sent.
function RequestDetail({ request, onClose }) {
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  const edge = edgeOf(request);
  const present = (value) => value !== null && value !== undefined && value !== '';
  const edgeRows = Object.entries(edge)
    .filter(([key, value]) => key !== 'metadata' && present(value))
    .map(([key, value]) => [EDGE_HEADERS[key] || key, String(value)]);
  const rawRows = Object.entries(edge.metadata || {}).filter(([, value]) => present(value));
  const identity = [
    [tx('ui.webservers.client_ip'), request.clientIp],
    [tx('ui.webservers.recorded'), when(request.createdAt)],
  ].filter(([, value]) => present(value));
  const browser = [
    [tx('ui.webservers.host'), request.host],
    [tx('ui.webservers.origin'), request.origin],
    [tx('ui.webservers.referrer'), request.referrer],
    [tx('ui.webservers.user_agent'), request.userAgent],
    [tx('ui.webservers.language'), request.acceptLanguage],
    [tx('ui.webservers.accept_encoding'), request.acceptEncoding],
    [tx('ui.webservers.content_type'), request.contentType],
    [tx('ui.webservers.request_size'), present(request.requestBytes) ? bytes(request.requestBytes) : null],
    [tx('ui.webservers.http_version'), request.httpVersion],
    [tx('ui.webservers.remote_address'), request.remoteAddress],
    [tx('ui.webservers.query_keys'), Array.isArray(request.queryKeys) ? request.queryKeys.join(', ') : null],
  ].filter(([, value]) => present(value));
  const tone = httpTone(request.status);
  return (
    <div className="xo-mob-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <motion.div className="xo-mob-dialog is-wide xo-web-request" role="dialog" aria-modal="true" aria-labelledby="xo-web-request-title"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
        <header>
          <span className="xo-mob-dialog-icon"><Globe2 aria-hidden="true" /></span>
          <div>
            <h2 id="xo-web-request-title"><span className="xo-web-method">{request.method}</span><code>{request.route || request.path}</code></h2>
            <p>{request.serviceName || tx('ui.webservers.unassigned')} · {when(request.createdAt)}</p>
          </div>
          <button type="button" className="xo-mob-icon-button" aria-label={tx('ui.webservers.close')} onClick={onClose}><X /></button>
        </header>
        <div className="xo-mob-recipient-facts">
          <span><small>{tx('ui.webservers.status')}</small><strong className={`is-${tone}`}>{request.status}</strong></span>
          <span><small>{tx('ui.webservers.latency')}</small><strong>{latency(request.durationUs)}</strong></span>
          <span><small>{tx('ui.webservers.response_size')}</small><strong>{bytes(request.responseBytes)}</strong></span>
          <span><small>{tx('ui.webservers.location')}</small><strong className="xo-web-fact-place"><Flag code={edge.countryCode} />{placeOf(request) || '—'}</strong></span>
        </div>
        <div className="xo-web-request-body">
          <Fields title={tx('ui.webservers.request_identity')} rows={identity} />
          <Fields title={tx('ui.webservers.edge_context')} rows={edgeRows} mono />
          {rawRows.length > 0 && (
            <details className="xo-web-raw">
              <summary><span>{tx('ui.webservers.raw_edge_headers')}</span><b>{NUMBER.format(rawRows.length)}</b></summary>
              <dl className="xo-web-fields is-mono">
                {rawRows.map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{String(value)}</dd></div>)}
              </dl>
            </details>
          )}
          <Fields title={tx('ui.webservers.browser_context')} rows={browser} />
        </div>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------- pages

function Overview() {
  const [days, setDays] = useState(30);
  const [opened, setOpened] = useState(null);
  const now = useNow(30000);
  const loadOverview = useCallback(() => loadWebserverOverview(days), [days]);
  const loadReport = useCallback(() => loadWebserverAnalytics({ days }), [days]);
  const loadLatest = useCallback(() => loadWebserverRequests({ limit: PAGE }), []);
  const overview = useLiveResource(['webservers'], loadOverview, { reconcileMs: 60000 });
  const report = useLiveResource(['webservers'], loadReport, { reconcileMs: 60000 });
  const latest = useLiveResource(['webservers'], loadLatest, { reconcileMs: 30000 });

  if ([overview, report].some((resource) => resource.loading && resource.data === null)) {
    return <Loading label={tx('ui.webservers.reading_platform')} />;
  }
  const broken = [overview, report].find((resource) => resource.error && resource.data === null);
  if (broken) return <Failed message={broken.error} onRetry={broken.refresh} />;

  const data = overview.data;
  const traffic = report.data;
  const requests = latest.data ? latest.data.requests : [];
  const series = filled(traffic);
  const submissions = data.services.reduce((sum, service) => sum + service.periodSubmissions, 0);
  const everSubmitted = data.services.reduce((sum, service) => sum + service.totalSubmissions, 0);
  const receiving = data.services.filter((service) => service.requests > 0).length;
  const warning = overview.error || report.error || latest.error;

  return (
    <div className="xo-platform-page xo-web-page">
      <Head title={tx('ui.webservers.title')} subtitle={tx('ui.webservers.overview_subtitle')}>
        <RangeTabs value={days} onChange={setDays} />
      </Head>
      {warning && <p className="xo-alert t-warn" role="alert">{warning}</p>}

      <div className="xo-mob-hero">
        <Panel title={tx('ui.webservers.map_title')} detail={tx('ui.webservers.map_detail')} className="xo-mob-hero-map">
          <TrafficMap report={traffic} requests={requests} height={440} />
        </Panel>
        <div className="xo-mob-hero-side">
          <LastHour requests={requests} now={now} onOpen={setOpened} />
          <Places report={traffic} />
        </div>
      </div>

      <KpiRow items={[
        { label: tx('ui.webservers.requests'), value: COMPACT.format(data.totals.requests),
          detail: tx('ui.webservers.served_value', { value: bytes(data.totals.responseBytes) }), trend: series.map((point) => point.requests) },
        { label: tx('ui.webservers.visitors'), value: COMPACT.format(data.totals.uniqueClients),
          detail: tx('ui.webservers.unique_ips'), trend: series.map((point) => point.uniqueClients) },
        { label: tx('ui.webservers.submissions'), value: NUMBER.format(submissions),
          detail: tx('ui.webservers.submissions_all', { count: NUMBER.format(everSubmitted) }) },
        { label: tx('ui.webservers.errors'), value: NUMBER.format(failures(data.totals)),
          detail: counted('ui.webservers.server_error_count', data.totals.serverErrors), trend: series.map((point) => point.errors) },
      ]} />

      <div className="xo-mob-row is-wide-left is-even">
        <Panel title={tx('ui.webservers.traffic_over_time')} detail={tx('ui.webservers.traffic_detail')}>
          <TrafficChart report={traffic} />
        </Panel>
        <Panel title={tx('ui.webservers.latest_requests')} detail={tx('ui.webservers.latest_requests_detail')}
          aside={<Link className="xo-mob-link" to={`/xomics/webservers/analytics?days=${days}#requests`}>{tx('ui.webservers.all_requests')}<ChevronRight aria-hidden="true" /></Link>}>
          <LatestRequests requests={requests.slice(0, 5)} now={now} onOpen={setOpened} />
        </Panel>
      </div>

      <Panel title={tx('ui.webservers.service_activity')} detail={tx('ui.webservers.service_activity_detail')}
        aside={<span className="xo-mob-aside-note">{tx('ui.webservers.receiving_value', { count: NUMBER.format(receiving), total: NUMBER.format(data.totals.activeServices) })}</span>}>
        <Services services={data.services} days={days} now={now} />
      </Panel>

      <div className="xo-mob-row is-halves is-even">
        <Panel title={tx('ui.webservers.response_status')} detail={tx('ui.webservers.response_status_detail')}>
          <Statuses statuses={traffic.statuses} />
        </Panel>
        <Panel title={tx('ui.webservers.recent_submissions')} detail={tx('ui.webservers.recent_submissions_detail')}>
          <Submissions submissions={data.recentSubmissions} now={now} />
        </Panel>
      </div>

      <EdgeFields coverage={data.cloudflareCoverage} />

      <AnimatePresence>
        {opened && <RequestDetail key={opened.id} request={opened} onClose={() => setOpened(null)} />}
      </AnimatePresence>
    </div>
  );
}

function Analytics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const asked = Number(searchParams.get('days'));
  const days = RANGES.includes(asked) ? asked : 30;
  const service = String(searchParams.get('service') || '').trim();
  const [opened, setOpened] = useState(null);
  // Older requests read with "show older", kept for the service they were read for.
  const [older, setOlder] = useState({ service: '', requests: [], done: false });
  const [shown, setShown] = useState({ service: '', count: SHOWN });
  const [reading, setReading] = useState(false);
  const [olderError, setOlderError] = useState(null);
  const explorer = useRef(null);
  const scrolled = useRef(false);
  const now = useNow(30000);
  const loadOverview = useCallback(() => loadWebserverOverview(days), [days]);
  const loadReport = useCallback(() => loadWebserverAnalytics({ days, service }), [days, service]);
  const loadRequests = useCallback(() => loadWebserverRequests({ service, limit: PAGE }), [service]);
  const overview = useLiveResource(['webservers'], loadOverview, { reconcileMs: 60000 });
  const report = useLiveResource(['webservers'], loadReport, { reconcileMs: 60000 });
  const first = useLiveResource(['webservers'], loadRequests, { reconcileMs: 60000 });

  const kept = older.service === service ? older : { service, requests: [], done: false };
  const listed = useMemo(() => {
    const byId = new Map();
    for (const request of [...(first.data ? first.data.requests : []), ...kept.requests]) byId.set(request.id, request);
    return [...byId.values()].sort((a, b) => b.id - a.id);
  }, [first.data, kept.requests]);
  const unread = kept.requests.length > 0 ? !kept.done : Boolean(first.data && first.data.hasMore);
  const showing = shown.service === service ? shown.count : SHOWN;
  const more = showing < listed.length || unread;

  // Arriving from "All requests" lands on the list once it is there.
  useEffect(() => {
    if (scrolled.current || location.hash !== '#requests' || !first.data || !explorer.current) return;
    scrolled.current = true;
    explorer.current.scrollIntoView({ block: 'start' });
  }, [location.hash, first.data]);

  function change(key, value) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, String(value)); else next.delete(key);
    if (key !== 'days') next.set('days', String(days));
    setSearchParams(next, { replace: true });
  }

  // Shows the next requests, reading an older page when those already read run
  // out. What is listed is kept as it is, so requests arriving live on top never
  // leave a gap between the pages.
  async function showOlder() {
    if (reading || listed.length === 0) return;
    const next = { service, count: showing + SHOWN };
    if (next.count <= listed.length || !unread) {
      setShown(next);
      return;
    }
    setReading(true);
    setOlderError(null);
    try {
      const page = await loadWebserverRequests({ service, limit: PAGE, beforeId: listed[listed.length - 1].id });
      setOlder({ service, requests: [...listed, ...page.requests], done: !page.hasMore });
      setShown(next);
    } catch (error) {
      setOlderError(error.message);
    } finally {
      setReading(false);
    }
  }

  if ([overview, report].some((resource) => resource.loading && resource.data === null)) {
    return <Loading label={tx('ui.webservers.reading_analytics')} />;
  }
  const broken = [report, overview].find((resource) => resource.error && resource.data === null);
  if (broken) return <Failed message={broken.error} onRetry={broken.refresh} />;

  const data = report.data;
  const series = filled(data);
  const warning = report.error || overview.error || first.error || olderError;
  const options = [
    { value: '', label: tx('ui.webservers.all_services'), count: overview.data.totals.requests },
    ...overview.data.services.map((item) => ({ value: item.code, label: item.displayName, count: item.requests })),
  ];

  return (
    <div className="xo-platform-page xo-web-page">
      <Head title={tx('ui.webservers.analytics_title')} subtitle={tx('ui.webservers.analytics_subtitle')}>
        <div className="xo-web-head-controls">
          <FilterMenu icon={Server} label={tx('ui.webservers.service_filter')} value={service} options={options}
            onChange={(value) => change('service', value)} />
          <RangeTabs value={days} onChange={(value) => change('days', value)} />
        </div>
      </Head>
      {warning && <p className="xo-alert t-warn" role="alert">{warning}</p>}

      <KpiRow items={[
        { label: tx('ui.webservers.requests'), value: COMPACT.format(data.totals.requests),
          detail: tx('ui.webservers.served_value', { value: bytes(data.totals.responseBytes) }), trend: series.map((point) => point.requests) },
        { label: tx('ui.webservers.visitors'), value: COMPACT.format(data.totals.uniqueClients),
          detail: tx('ui.webservers.unique_ips'), trend: series.map((point) => point.uniqueClients) },
        { label: tx('ui.webservers.avg_latency'), value: latency(data.totals.avgDurationUs),
          detail: tx('ui.webservers.maximum_value', { value: latency(data.totals.maxDurationUs) }) },
        { label: tx('ui.webservers.errors'), value: NUMBER.format(failures(data.totals)),
          detail: counted('ui.webservers.server_error_count', data.totals.serverErrors), trend: series.map((point) => point.errors) },
      ]} />

      <Panel title={tx('ui.webservers.traffic_over_time')} detail={tx('ui.webservers.traffic_detail')}>
        <TrafficChart report={data} />
      </Panel>

      <div className="xo-mob-row is-halves is-even">
        <Panel title={tx('ui.webservers.routes_title')} detail={tx('ui.webservers.routes_detail')}>
          <Routes routes={data.routes} />
        </Panel>
        <Panel title={tx('ui.webservers.response_status')} detail={tx('ui.webservers.response_status_detail')}>
          <Statuses statuses={data.statuses} />
        </Panel>
      </div>

      <div className="xo-mob-row is-wide-left is-even">
        <Panel title={tx('ui.webservers.map_title')} detail={tx('ui.webservers.map_detail')} className="xo-mob-hero-map">
          <TrafficMap report={data} requests={listed} height={360} />
        </Panel>
        <Places report={data} />
      </div>

      <div className="xo-mob-row is-thirds is-even">
        <Panel title={tx('ui.webservers.methods_title')} detail={tx('ui.webservers.methods_detail')}>
          <Ranked empty={tx('ui.webservers.no_traffic')}
            rows={data.methods.map((row) => ({ key: row.method, requests: row.requests, name: <span className="xo-web-method">{row.method}</span> }))} />
        </Panel>
        <Panel title={tx('ui.webservers.referrers_title')} detail={tx('ui.webservers.referrers_detail')}>
          <Ranked empty={tx('ui.webservers.no_referrers')}
            rows={data.referrers.map((row) => ({ key: row.referrer, requests: row.requests, name: referrerName(row.referrer) }))} />
        </Panel>
        <Panel title={tx('ui.webservers.edge_locations')} detail={tx('ui.webservers.edge_locations_detail')}>
          <Ranked empty={tx('ui.webservers.no_traffic')}
            rows={data.colos.map((row) => ({ key: row.code, requests: row.requests,
              name: row.code === '---' ? <strong>{tx('ui.webservers.edge_unknown')}</strong> : <span className="xo-web-method is-place">{row.code}</span> }))} />
        </Panel>
      </div>

      <div ref={explorer} className="xo-mob-anchor">
        <Panel title={tx('ui.webservers.request_explorer')} detail={tx('ui.webservers.request_list_detail')}>
          {first.loading && first.data === null ? <Loading /> : <RequestTable requests={listed.slice(0, showing)} now={now} onOpen={setOpened} />}
          {more && (
            <button type="button" className="xo-mob-more" onClick={showOlder} disabled={reading}>
              {reading ? tx('ui.webservers.reading_older') : tx('ui.webservers.show_older')}
            </button>
          )}
        </Panel>
      </div>

      <AnimatePresence>
        {opened && <RequestDetail key={opened.id} request={opened} onClose={() => setOpened(null)} />}
      </AnimatePresence>
    </div>
  );
}

export default function XomicsWebservers({ view = 'overview' }) {
  return view === 'analytics' ? <Analytics /> : <Overview />;
}
